import { CoreApiClient } from '../../core/client'
import { withQuery } from '../../core/query'
import type { PageResult } from '../../core/types'

export interface DecorationPageQueryParams {
  keyword?: string
  published?: boolean | null
  pageType?: string
  pageNo?: number
  pageSize?: number
}

export type DecorationComponentType =
  | 'search'
  | 'banner'
  | 'nav'
  | 'notice'
  | 'image'
  | 'product-list'
  | 'rich-text'
  | 'blank'
  | 'divider'
  | string

export interface DecorationComponentDataSource {
  code: string
  params?: Record<string, unknown>
  bindings?: Record<string, string>
}

export interface DecorationNavigationTarget {
  targetCode: string
  targetName?: string
  moduleCode: string
  targetType: string
  pageCode: string
  routePath: string
  routeTemplate?: string
  params?: Record<string, string | number | boolean>
  paramBindings?: Record<string, string>
  requiredPermission?: string
  loginRequired?: boolean
}

export interface DecorationComponent {
  id: string
  type: DecorationComponentType
  title: string
  enabled: boolean
  dataSource?: DecorationComponentDataSource | null
  navigation?: DecorationNavigationTarget | null
  props: Record<string, unknown>
  style?: Record<string, unknown>
}

export interface DecorationTabbarItem {
  code: string
  text: string
  pageCode: string
  icon?: string
  activeIcon?: string
  routePath?: string
  loginRequired?: boolean
  badgeSource?: { code: string; field?: string }
}

export interface DecorationTabbarSchema {
  mode?: 'DYNAMIC' | string
  position?: 'bottom' | 'top' | string
  color?: string
  selectedColor?: string
  backgroundColor?: string
  borderColor?: string
  items: DecorationTabbarItem[]
}

export interface DecorationSchema {
  version: string
  moduleCode: string
  pageCode: string
  page: {
    title: string
    backgroundColor?: string
    themeColor?: string
    [key: string]: unknown
  }
  components: DecorationComponent[]
  tabbar?: DecorationTabbarSchema
}

/** Canonical client decoration platform vocabulary shared by API consumers. */
export type DecorationPlatform = 'UNI_APP' | 'APP' | 'H5' | 'MINI_PROGRAM' | 'WEB'

export const DECORATION_PLATFORMS = [
  'UNI_APP',
  'APP',
  'H5',
  'MINI_PROGRAM',
  'WEB'
] as const satisfies readonly DecorationPlatform[]

export const DECORATION_PLATFORM_METADATA = {
  UNI_APP: { clientSelectable: true, nativeOnly: false },
  APP: { clientSelectable: true, nativeOnly: false },
  H5: { clientSelectable: true, nativeOnly: false },
  MINI_PROGRAM: { clientSelectable: true, nativeOnly: false },
  WEB: { clientSelectable: true, nativeOnly: false }
} as const satisfies Record<DecorationPlatform, { clientSelectable: boolean; nativeOnly: boolean }>

/** Native implementation capabilities are metadata only, never selectable client platforms. */
export type DecorationNativeCapability = 'FLUTTER'

export const DECORATION_NATIVE_CAPABILITIES = ['FLUTTER'] as const satisfies readonly DecorationNativeCapability[]

export const DECORATION_NATIVE_CAPABILITY_METADATA = {
  FLUTTER: { clientSelectable: false, nativeOnly: true }
} as const satisfies Record<DecorationNativeCapability, { clientSelectable: boolean; nativeOnly: boolean }>

const DECORATION_PLATFORM_ALIASES: Record<string, DecorationPlatform> = {
  UNIAPP: 'UNI_APP',
  PC_WEB: 'WEB'
}

export function normalizeDecorationPlatform(value: unknown): DecorationPlatform | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toUpperCase()
  if (!normalized) return null
  const canonical = DECORATION_PLATFORM_ALIASES[normalized] ?? normalized
  return (DECORATION_PLATFORMS as readonly string[]).includes(canonical)
    ? (canonical as DecorationPlatform)
    : null
}

/** Parse a platform array or its JSON representation, failing closed on unknown/native values. */
export function parseDecorationPlatforms(value: unknown): DecorationPlatform[] {
  let input: unknown = value
  if (typeof input === 'string') {
    try {
      input = JSON.parse(input)
    } catch {
      return []
    }
  }
  if (!Array.isArray(input)) return []
  const parsed: DecorationPlatform[] = []
  for (const item of input) {
    const platform = normalizeDecorationPlatform(item)
    if (platform && !parsed.includes(platform)) parsed.push(platform)
  }
  return parsed
}

/** Serialize platform selections for the existing JSON request fields. */
export function serializeDecorationPlatforms(value: unknown): string {
  return JSON.stringify(parseDecorationPlatforms(value))
}

/** Serialize the business-template supported-platform request field without accepting native aliases. */
export function serializeDecorationTemplatePlatforms(value: unknown): string {
  return serializeDecorationPlatforms(value)
}

export function isDecorationPlatform(value: unknown): value is DecorationPlatform {
  return normalizeDecorationPlatform(value) !== null
}

export interface DecorationPage {
  id: number
  tenantId: number
  moduleCode: string
  pageCode: string
  pageTitle: string
  pageType: string
  routePath: string
  status: string
  published: boolean
  schemaJson: string
  publishedSchemaJson: string
  themeJson: string
  remark: string
  createdAt: string
  updatedAt: string
  publishedAt?: string | null
}

/** Published-only projection returned by ordinary client decoration APIs. */
export interface ClientDecorationPageView {
  id: number
  tenantId: number
  moduleCode: string
  pageCode: string
  pageTitle: string
  pageType: string
  routePath: string
  published: boolean
  publishedSchemaJson: string
  publishedAt?: string | null
  supportedPlatforms?: DecorationPlatform[] | null
  supportedPlatformsJson?: string | null
}

/** Semantic alias for consumers that want to make the publication boundary explicit. */
export type PublishedDecorationPage = ClientDecorationPageView

export interface DecorationPageQrCode {
  pageCode: string
  publicUrl: string
  imageUrl: string
  h5Url?: string
  miniAppId?: string
  miniAppPagePath?: string
  webUrl?: string | null
  channel?: string | null
  platform?: DecorationPlatform | null
  provenance?: string | null
  status: string
  publishedAt?: string | null
}

export interface DecorationPagePreviewMetadata {
  h5Url?: string
  miniAppId?: string
  miniAppPagePath?: string
  webUrl?: string | null
  channel?: string | null
  platform?: DecorationPlatform | null
  provenance?: string | null
}

export interface LegacyConversionWarning {
  code: string
  level: string
  path: string
  message: string
}

export interface LegacyDecorationImportRequest {
  pageCode: string
  pageTitle?: string
  pageType?: string
  routePath?: string
  legacyJson: string
}

export interface LegacyDecorationImportPreview {
  schemaJson: string
  warnings: LegacyConversionWarning[]
  unsupported: LegacyConversionWarning[]
}

export interface LegacyDecorationImportResult {
  page: DecorationPage
  warnings: LegacyConversionWarning[]
  unsupported: LegacyConversionWarning[]
}

export interface DecorationComponentDefinition {
  type: string
  label: string
  groupName: string
  description: string
  icon: string
  enabled: boolean
  sortOrder: number
  defaultPropsJson: string
  propertySchemaJson: string
  styleSchemaJson: string
  dataSourceSchemaJson: string
  componentScope: string
  ownerModuleCode: string
  supportedPlatformsJson: string
  protocolVersion: string
}

export interface DecorationPageReference {
  sourceModuleCode: string
  sourcePageCode: string
  sourcePageTitle: string
  sourceComponentId?: string | null
  sourcePath: string
  targetModuleCode: string
  targetPageCode: string
}

export interface DecorationDataSourceDefinition {
  sourceCode: string
  sourceName: string
  moduleCode: string
  scope: string
  description: string
  endpoint: string
  method: string
  requiredPermission: string
  paramsSchemaJson: string
  sampleJson: string
}

export interface DecorationLinkTargetDefinition {
  targetCode: string
  targetName: string
  moduleCode: string
  groupName: string
  targetType: string
  pageCode: string
  routePath: string
  requiredPermission: string
  loginRequired: boolean
  enabled: boolean
  sortOrder: number
  paramsSchemaJson: string
  supportedPlatformsJson: string
}

export interface DecorationNavigationTarget {
  targetCode: string
  targetName?: string
  moduleCode: string
  targetType: string
  pageCode: string
  routePath: string
  routeTemplate?: string
  params?: Record<string, string | number | boolean>
  paramBindings?: Record<string, string>
  requiredPermission?: string
  loginRequired?: boolean
}

export interface DecorationMarketTemplate {
  id: number
  tenantId?: number | null
  moduleCode: string
  templateCode: string
  templateName: string
  templateType: string
  pageType: string
  coverImageUrl: string
  description: string
  schemaJson: string
  themeJson: string
  status: string
  publicTemplate: boolean
  createdBy?: number | null
  createdAt: string
  updatedAt: string
}

export interface DecorationTemplateQueryParams {
  keyword?: string
  pageType?: string
  publicOnly?: boolean | null
  page?: number
  pageSize?: number
}

export interface SaveDecorationPageRequest {
  tenantId?: number | null
  moduleCode?: string | null
  pageCode?: string | null
  pageTitle: string
  pageType: string
  routePath: string
  status: string
  schemaJson: string
  themeJson: string
  remark: string
}

export interface CopyDecorationPageRequest {
  targetPageCode: string
  targetPageTitle: string
  targetRoutePath?: string | null
}

export interface LegacyConversionWarning {
  code: string
  level: string
  path: string
  message: string
}

export interface LegacyDecorationImportRequest {
  pageCode: string
  pageTitle?: string
  pageType?: string
  routePath?: string
  legacyJson: string
}

export interface LegacyDecorationImportPreview {
  schemaJson: string
  warnings: LegacyConversionWarning[]
  unsupported: LegacyConversionWarning[]
}

export interface LegacyDecorationImportResult {
  page: DecorationPage
  warnings: LegacyConversionWarning[]
  unsupported: LegacyConversionWarning[]
}

export interface SaveDecorationTemplateRequest {
  tenantId?: number | null
  moduleCode?: string | null
  templateCode: string
  templateName: string
  templateType: string
  pageType: string
  coverImageUrl: string
  description: string
  schemaJson: string
  themeJson: string
  status: string
  publicTemplate: boolean
  createdBy?: number | null
}

export interface InstallDecorationTemplateRequest {
  tenantId?: number | null
  moduleCode?: string | null
  templateCode?: string | null
  targetPageCode: string
  targetPageTitle: string
  operatorId?: number | null
}

export interface UpdateDecorationTemplateStatusRequest {
  status: 'LISTED' | 'ACTIVE' | 'DISABLED' | 'RECYCLED' | string
}

export interface DecorationTemplateVersion {
  id: number
  templateId: number
  moduleCode: string
  templateCode: string
  templateVersion: string
  templateName: string
  pageType: string
  protocolRange: string
  schemaJson: string
  themeJson: string
  lockPolicyJson: string
  entityPresentationSchemaJson: string
  supportedPlatformsJson: string
  minimumClientCapabilitiesJson: string
  status: string
  schemaDigest: string
  policyDigest: string
  createdBy: number | null
  createdAt: string
  publishedAt: string | null
}

export interface DecorationPageTemplateBinding {
  tenantId: number
  moduleCode: string
  pageCode: string
  templateId: number
  templateCode: string
  draftTemplateVersionId: number | null
  draftTemplateVersion: string | null
  publishedTemplateVersionId: number | null
  publishedTemplateVersion: string | null
  baseSchemaDigest: string
  overridesJson: string
  conflictsJson: string
  upgradeStatus: string
  appliedBy: number | null
  appliedAt: string | null
  version: number
}

export interface DecorationTemplateUpgradePreview {
  fromVersion: string
  targetVersion: string
  candidateSchemaJson: string
  retainedOverridesJson: string
  conflictsJson: string
  applicable: boolean
}

export interface SaveDecorationTemplateVersionRequest {
  moduleCode: string
  templateCode: string
  templateVersion: string
  templateName: string
  pageType: string
  protocolRange: string
  schemaJson: string
  themeJson: string
  lockPolicyJson: string
  entityPresentationSchemaJson: string
  supportedPlatformsJson: string
  minimumClientCapabilitiesJson: string
  status: string
  createdBy: number | null
}

export interface UpdateDecorationTemplateVersionStatusRequest {
  moduleCode: string
  templateCode: string
  templateVersion: string
  status: string
  updatedBy: number | null
}

export interface InstallDecorationTemplateVersionRequest {
  tenantId: number | null
  moduleCode: string
  templateCode: string
  templateVersion: string
  targetPageCode: string
  targetPageTitle: string
  operatorId: number | null
}

export interface PreviewDecorationTemplateUpgradeRequest {
  tenantId: number | null
  moduleCode: string
  pageCode: string
  targetTemplateVersion: string
  resolutionsJson: string
  expectedBindingVersion: number
}

export interface ApplyDecorationTemplateUpgradeRequest {
  tenantId: number | null
  moduleCode: string
  pageCode: string
  targetTemplateVersion: string
  resolutionsJson: string
  expectedBindingVersion: number
  operatorId: number | null
}

export interface DecorationPageQuery {
  tenantId?: number | null
  moduleCode?: string | null
  keyword?: string
  published?: boolean | null
}

export interface StandardSaasDecorationBlock {
  code: string
  name: string
  blockType: string
  enabled: boolean
  sortOrder: number
  summary: string
}

export interface StandardSaasDecorationChannel {
  code: string
  name: string
  status: string
  entry: string
}

export interface StandardSaasDecoration {
  moduleCode: string
  moduleName: string
  title: string
  themeColor: string
  previewPath: string
  blocks: StandardSaasDecorationBlock[]
  channels: StandardSaasDecorationChannel[]
  updatedAt: string
}

export const DECORATION_PERMISSIONS = {
  pageView: 'decoration:page:view',
  pageManage: 'decoration:page:manage',
  componentView: 'decoration:component:view',
  datasourceView: 'decoration:datasource:view',
  templateView: 'decoration:template:view',
  templateManage: 'decoration:template:manage',
  clientView: 'decoration:client:view'
} as const

export const DECORATION_PERMISSION_LIST = Object.values(DECORATION_PERMISSIONS)

/** Decoration API methods mixin / standalone functions */
export class DecorationApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  getClientDecorationPage(pageCode = 'home') {
    return this.client.get<ClientDecorationPageView>(`/api/client/decoration/${encodeURIComponent(pageCode)}`)
  }

  getClientModuleDecorationPage(moduleCode: string, pageCode = 'home') {
    return this.client.get<ClientDecorationPageView>(
      `/api/client/${encodeURIComponent(moduleCode)}/decoration/${encodeURIComponent(pageCode)}`
    )
  }

  getClientContentDecorationPage(siteId: number, pageCode = 'home') {
    return this.client.get<ClientDecorationPageView>(
      withQuery(`/api/client/system/content-decoration/${encodeURIComponent(pageCode)}`, { siteId })
    )
  }

  getClientMallDecorationPage(pageCode = 'home') {
    return this.client.get<ClientDecorationPageView>(
      `/api/client/mall/decoration/${encodeURIComponent(pageCode)}`
    )
  }

  getSaasDecorationPages(query: DecorationPageQueryParams = {}) {
    return this.client.get<PageResult<DecorationPage>>(withQuery('/api/saas/decoration/pages', query))
  }

  getSaasDecorationPage(pageCode = 'home') {
    return this.client.get<DecorationPage>(`/api/saas/decoration/pages/${encodeURIComponent(pageCode)}`)
  }

  getSaasDecorationPageReferences(pageCode = 'home') {
    return this.client.get<DecorationPageReference[]>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/references`
    )
  }

  saveSaasDecorationPage(pageCode: string, body: SaveDecorationPageRequest) {
    return this.client.put<DecorationPage>(`/api/saas/decoration/pages/${encodeURIComponent(pageCode)}`, body)
  }

  copySaasDecorationPage(pageCode: string, body: CopyDecorationPageRequest) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/copy`,
      body
    )
  }

  deleteSaasDecorationPage(pageCode: string) {
    return this.client.delete<boolean>(`/api/saas/decoration/pages/${encodeURIComponent(pageCode)}`)
  }

  publishSaasDecorationPage(pageCode = 'home') {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/publish`,
      {}
    )
  }

  issueSaasDecorationPageQrCode(pageCode = 'home') {
    return this.client.post<DecorationPageQrCode>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/qrcode`,
      {}
    )
  }

  getSaasDecorationPagePreviewMetadata(pageCode = 'home') {
    return this.client.get<DecorationPagePreviewMetadata>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/preview-metadata`
    )
  }

  resetSaasDecorationPage(pageCode = 'home') {
    return this.client.post<DecorationPage>(`/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/reset`, {})
  }

  validateSaasDecorationPage(pageCode: string, body: { schemaJson: string }) {
    return this.client.post<{ valid: boolean; errors?: { code: string; message: string }[] }>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/validate`,
      body
    )
  }

  getSaasDecorationComponents() {
    return this.client.get<DecorationComponentDefinition[]>('/api/saas/decoration/components')
  }

  getSaasDecorationDataSources() {
    return this.client.get<DecorationDataSourceDefinition[]>('/api/saas/decoration/data-sources')
  }

  getSaasDecorationLinkTargets() {
    return this.client.get<DecorationLinkTargetDefinition[]>('/api/saas/decoration/link-targets')
  }

  getSaasDecorationTemplates(query: DecorationTemplateQueryParams = {}) {
    return this.client.get<PageResult<DecorationMarketTemplate>>(
      withQuery('/api/saas/decoration/templates', query)
    )
  }

  saveSaasDecorationTemplate(body: SaveDecorationTemplateRequest) {
    return this.client.post<DecorationMarketTemplate>('/api/saas/decoration/templates', body)
  }

  installSaasDecorationTemplate(templateCode: string, body: InstallDecorationTemplateRequest) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/templates/${encodeURIComponent(templateCode)}/install`,
      body
    )
  }

  updateSaasDecorationTemplateStatus(templateCode: string, body: UpdateDecorationTemplateStatusRequest) {
    return this.client.put<DecorationMarketTemplate>(
      `/api/saas/decoration/templates/${encodeURIComponent(templateCode)}/status`,
      body
    )
  }

  recycleSaasDecorationTemplate(templateCode: string) {
    return this.client.delete<DecorationMarketTemplate>(
      `/api/saas/decoration/templates/${encodeURIComponent(templateCode)}`
    )
  }

  restoreSaasDecorationTemplate(templateCode: string) {
    return this.client.post<DecorationMarketTemplate>(
      `/api/saas/decoration/templates/${encodeURIComponent(templateCode)}/restore`
    )
  }

  deleteSaasDecorationTemplatePermanently(templateCode: string) {
    return this.client.delete<void>(
      `/api/saas/decoration/templates/${encodeURIComponent(templateCode)}/permanent`
    )
  }

  getSaasDecorationTemplateBinding(pageCode: string) {
    return this.client.get<DecorationPageTemplateBinding>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/template-binding`
    )
  }

  getSaasDecorationBusinessTemplateVersions(templateCode: string, pageCode?: string) {
    return this.client.get<DecorationTemplateVersion[]>(
      withQuery(
        `/api/saas/decoration/business-templates/${encodeURIComponent(templateCode)}/versions`,
        { pageCode }
      )
    )
  }

  installSaasDecorationBusinessTemplateVersion(
    templateCode: string,
    templateVersion: string,
    body: InstallDecorationTemplateVersionRequest
  ) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/business-templates/${encodeURIComponent(templateCode)}/versions/${encodeURIComponent(templateVersion)}/install`,
      body
    )
  }

  previewSaasDecorationTemplateUpgrade(pageCode: string, body: PreviewDecorationTemplateUpgradeRequest) {
    return this.client.post<DecorationTemplateUpgradePreview>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/template-upgrade/preview`,
      body
    )
  }

  upgradeSaasDecorationTemplate(pageCode: string, body: ApplyDecorationTemplateUpgradeRequest) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/template-upgrade/apply`,
      body
    )
  }

  applySaasDecorationTemplateUpgrade(pageCode: string, body: ApplyDecorationTemplateUpgradeRequest) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/template-upgrade/apply`,
      body
    )
  }

  previewSaasDecorationLegacyImport(body: LegacyDecorationImportRequest) {
    return this.client.post<LegacyDecorationImportPreview>('/api/saas/decoration/legacy/preview', body)
  }

  importSaasDecorationLegacy(body: LegacyDecorationImportRequest) {
    return this.client.post<LegacyDecorationImportResult>('/api/saas/decoration/legacy/import', body)
  }

  revertSaasDecorationTemplate(pageCode: string) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/pages/${encodeURIComponent(pageCode)}/template-revert`,
      {}
    )
  }

  getSaasModuleDecorationPages(moduleCode: string, query: DecorationPageQueryParams = {}) {
    return this.client.get<PageResult<DecorationPage>>(withQuery(`/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages`, query))
  }

  getSaasModuleDecorationPage(moduleCode: string, pageCode = 'home') {
    return this.client.get<DecorationPage>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}`
    )
  }

  getSaasModuleDecorationPageReferences(moduleCode: string, pageCode = 'home') {
    return this.client.get<DecorationPageReference[]>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}/references`
    )
  }

  saveSaasModuleDecorationPage(moduleCode: string, pageCode: string, body: SaveDecorationPageRequest) {
    return this.client.put<DecorationPage>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}`,
      body
    )
  }

  copySaasModuleDecorationPage(moduleCode: string, pageCode: string, body: CopyDecorationPageRequest) {
    return this.client.post<DecorationPage>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}/copy`,
      body
    )
  }

  deleteSaasModuleDecorationPage(moduleCode: string, pageCode: string) {
    return this.client.delete<void>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}`
    )
  }

  publishSaasModuleDecorationPage(moduleCode: string, pageCode = 'home') {
    return this.client.post<DecorationPage>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}/publish`
    )
  }

  issueSaasModuleDecorationPageQrCode(moduleCode: string, pageCode = 'home') {
    return this.client.post<DecorationPageQrCode>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}/qrcode`
    )
  }

  getSaasModuleDecorationPagePreviewMetadata(moduleCode: string, pageCode = 'home') {
    return this.client.get<DecorationPagePreviewMetadata>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}/preview-metadata`
    )
  }

  resetSaasModuleDecorationPage(moduleCode: string, pageCode = 'home') {
    return this.client.post<DecorationPage>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/pages/${encodeURIComponent(pageCode)}/reset`
    )
  }

  getSaasModuleDecorationComponents(moduleCode: string) {
    return this.client.get<DecorationComponentDefinition[]>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/components`
    )
  }

  getSaasModuleDecorationDataSources(moduleCode: string) {
    return this.client.get<DecorationDataSourceDefinition[]>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/data-sources`
    )
  }

  getSaasModuleDecorationLinkTargets(moduleCode: string) {
    return this.client.get<DecorationLinkTargetDefinition[]>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/link-targets`
    )
  }

  getSaasModuleDecorationTemplates(
    moduleCode: string,
    query: DecorationTemplateQueryParams = {}
  ) {
    return this.client.get<PageResult<DecorationMarketTemplate>>(
      withQuery(`/api/saas/${encodeURIComponent(moduleCode)}/decoration/templates`, query)
    )
  }

  saveSaasModuleDecorationTemplate(moduleCode: string, body: SaveDecorationTemplateRequest) {
    return this.client.post<DecorationMarketTemplate>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/templates`,
      body
    )
  }

  installSaasModuleDecorationTemplate(
    moduleCode: string,
    templateCode: string,
    body: InstallDecorationTemplateRequest
  ) {
    return this.client.post<DecorationPage>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/templates/${encodeURIComponent(templateCode)}/install`,
      body
    )
  }

  updateSaasModuleDecorationTemplateStatus(
    moduleCode: string,
    templateCode: string,
    body: UpdateDecorationTemplateStatusRequest
  ) {
    return this.client.put<DecorationMarketTemplate>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/templates/${encodeURIComponent(templateCode)}/status`,
      body
    )
  }

  recycleSaasModuleDecorationTemplate(moduleCode: string, templateCode: string) {
    return this.client.delete<DecorationMarketTemplate>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/templates/${encodeURIComponent(templateCode)}`
    )
  }

  restoreSaasModuleDecorationTemplate(moduleCode: string, templateCode: string) {
    return this.client.post<DecorationMarketTemplate>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/templates/${encodeURIComponent(templateCode)}/restore`
    )
  }

  deleteSaasModuleDecorationTemplatePermanently(moduleCode: string, templateCode: string) {
    return this.client.delete<void>(
      `/api/saas/${encodeURIComponent(moduleCode)}/decoration/templates/${encodeURIComponent(templateCode)}/permanent`
    )
  }

  getSaasModuleDecorationTemplateBinding(moduleCode: string, pageCode: string) {
    return this.client.get<DecorationPageTemplateBinding>(
      `/api/saas/decoration/modules/${encodeURIComponent(moduleCode)}/pages/${encodeURIComponent(pageCode)}/template-binding`
    )
  }

  getSaasModuleDecorationBusinessTemplateVersions(moduleCode: string, templateCode: string, pageCode?: string) {
    return this.client.get<DecorationTemplateVersion[]>(
      withQuery(
        `/api/saas/decoration/modules/${encodeURIComponent(moduleCode)}/business-templates/${encodeURIComponent(templateCode)}/versions`,
        { pageCode }
      )
    )
  }

  installSaasModuleDecorationBusinessTemplateVersion(
    moduleCode: string,
    templateCode: string,
    templateVersion: string,
    body: InstallDecorationTemplateVersionRequest
  ) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/modules/${encodeURIComponent(moduleCode)}/business-templates/${encodeURIComponent(templateCode)}/versions/${encodeURIComponent(templateVersion)}/install`,
      body
    )
  }

  previewSaasModuleDecorationTemplateUpgrade(
    moduleCode: string,
    pageCode: string,
    body: PreviewDecorationTemplateUpgradeRequest
  ) {
    return this.client.post<DecorationTemplateUpgradePreview>(
      `/api/saas/decoration/modules/${encodeURIComponent(moduleCode)}/pages/${encodeURIComponent(pageCode)}/template-upgrade/preview`,
      body
    )
  }

  applySaasModuleDecorationTemplateUpgrade(
    moduleCode: string,
    pageCode: string,
    body: ApplyDecorationTemplateUpgradeRequest
  ) {
    return this.client.post<DecorationPage>(
      `/api/saas/decoration/modules/${encodeURIComponent(moduleCode)}/pages/${encodeURIComponent(pageCode)}/template-upgrade/apply`,
      body
    )
  }

  getSaasMallDecorationPages(query: { keyword?: string; published?: boolean | null } = {}) {
    return this.client.get<DecorationPage[]>(withQuery('/api/saas/mall/decoration/pages', query))
  }

  getSaasMallDecorationPage(pageCode = 'home') {
    return this.client.get<DecorationPage>(`/api/saas/mall/decoration/pages/${encodeURIComponent(pageCode)}`)
  }

  saveSaasMallDecorationPage(pageCode: string, body: SaveDecorationPageRequest) {
    return this.client.put<DecorationPage>(`/api/saas/mall/decoration/pages/${encodeURIComponent(pageCode)}`, body)
  }

  publishSaasMallDecorationPage(pageCode = 'home') {
    return this.client.post<DecorationPage>(`/api/saas/mall/decoration/pages/${encodeURIComponent(pageCode)}/publish`)
  }

  resetSaasMallDecorationPage(pageCode = 'home') {
    return this.client.post<DecorationPage>(`/api/saas/mall/decoration/pages/${encodeURIComponent(pageCode)}/reset`)
  }

  getSaasMallDecorationComponents() {
    return this.client.get<DecorationComponentDefinition[]>('/api/saas/mall/decoration/components')
  }

  getSaasMallDecorationDataSources() {
    return this.client.get<DecorationDataSourceDefinition[]>('/api/saas/mall/decoration/data-sources')
  }

  getSaasMallDecorationLinkTargets() {
    return this.client.get<DecorationLinkTargetDefinition[]>('/api/saas/mall/decoration/link-targets')
  }

  getSaasMallDecorationTemplates(query: DecorationTemplateQueryParams = {}) {
    return this.client.get<PageResult<DecorationMarketTemplate>>(
      withQuery('/api/saas/mall/decoration/templates', query)
    )
  }

  saveSaasMallDecorationTemplate(body: SaveDecorationTemplateRequest) {
    return this.client.post<DecorationMarketTemplate>('/api/saas/mall/decoration/templates', body)
  }

  installSaasMallDecorationTemplate(templateCode: string, body: InstallDecorationTemplateRequest) {
    return this.client.post<DecorationPage>(
      `/api/saas/mall/decoration/templates/${encodeURIComponent(templateCode)}/install`,
      body
    )
  }

  updateSaasMallDecorationTemplateStatus(templateCode: string, body: UpdateDecorationTemplateStatusRequest) {
    return this.client.put<DecorationMarketTemplate>(
      `/api/saas/mall/decoration/templates/${encodeURIComponent(templateCode)}/status`,
      body
    )
  }

  recycleSaasMallDecorationTemplate(templateCode: string) {
    return this.client.delete<DecorationMarketTemplate>(
      `/api/saas/mall/decoration/templates/${encodeURIComponent(templateCode)}`
    )
  }

  restoreSaasMallDecorationTemplate(templateCode: string) {
    return this.client.post<DecorationMarketTemplate>(
      `/api/saas/mall/decoration/templates/${encodeURIComponent(templateCode)}/restore`
    )
  }

  deleteSaasMallDecorationTemplatePermanently(templateCode: string) {
    return this.client.delete<void>(
      `/api/saas/mall/decoration/templates/${encodeURIComponent(templateCode)}/permanent`
    )
  }

  getAdminDecorationPages(query: DecorationPageQuery = {}) {
    return this.client.get<DecorationPage[]>(withQuery('/api/admin/decorations', query))
  }

  getAdminDecorationPage(tenantId: number, moduleCode: string, pageCode = 'home') {
    return this.client.get<DecorationPage>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}`
    )
  }

  saveAdminDecorationPage(tenantId: number, moduleCode: string, pageCode: string, body: SaveDecorationPageRequest) {
    return this.client.put<DecorationPage>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}`,
      body
    )
  }

  publishAdminDecorationPage(tenantId: number, moduleCode: string, pageCode = 'home') {
    return this.client.post<DecorationPage>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}/publish`
    )
  }

  resetAdminDecorationPage(tenantId: number, moduleCode: string, pageCode = 'home') {
    return this.client.post<DecorationPage>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}/reset`
    )
  }

  getAdminDecorationComponents(moduleCode: string) {
    return this.client.get<DecorationComponentDefinition[]>(`/api/admin/decorations/${encodeURIComponent(moduleCode)}/components`)
  }

  saveAdminDecorationComponent(body: DecorationComponentDefinition) {
    return this.client.post<DecorationComponentDefinition>('/api/admin/decoration/components', body)
  }

  deleteAdminDecorationComponent(type: string) {
    return this.client.delete<boolean>(`/api/admin/decoration/components/${encodeURIComponent(type)}`)
  }

  getAdminDecorationDataSources(moduleCode: string) {
    return this.client.get<DecorationDataSourceDefinition[]>(`/api/admin/decorations/${encodeURIComponent(moduleCode)}/data-sources`)
  }

  saveAdminDecorationDataSource(body: DecorationDataSourceDefinition) {
    return this.client.post<DecorationDataSourceDefinition>('/api/admin/decoration/datasources', body)
  }

  deleteAdminDecorationDataSource(sourceCode: string) {
    return this.client.delete<boolean>(`/api/admin/decoration/datasources/${encodeURIComponent(sourceCode)}`)
  }

  getAdminDecorationLinkTargets(tenantId: number, moduleCode: string) {
    return this.client.get<DecorationLinkTargetDefinition[]>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/link-targets`
    )
  }

  saveAdminDecorationLinkTarget(body: DecorationLinkTargetDefinition) {
    return this.client.post<DecorationLinkTargetDefinition>('/api/admin/decoration/link-targets', body)
  }

  deleteAdminDecorationLinkTarget(targetCode: string) {
    return this.client.delete<boolean>(`/api/admin/decoration/link-targets/${encodeURIComponent(targetCode)}`)
  }

  getAdminDecorationTemplates(
    tenantId: number,
    moduleCode: string,
    query: { keyword?: string; pageType?: string; publicOnly?: boolean | null } = {}
  ) {
    return this.client.get<DecorationMarketTemplate[]>(
      withQuery(`/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/templates`, query)
    )
  }

  saveAdminDecorationTemplate(tenantId: number, moduleCode: string, body: SaveDecorationTemplateRequest) {
    return this.client.post<DecorationMarketTemplate>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/templates`,
      body
    )
  }

  installAdminDecorationTemplate(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    body: InstallDecorationTemplateRequest
  ) {
    return this.client.post<DecorationPage>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/templates/${encodeURIComponent(templateCode)}/install`,
      body
    )
  }

  updateAdminDecorationTemplateStatus(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    body: UpdateDecorationTemplateStatusRequest
  ) {
    return this.client.put<DecorationMarketTemplate>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/templates/${encodeURIComponent(templateCode)}/status`,
      body
    )
  }

  recycleAdminDecorationTemplate(tenantId: number, moduleCode: string, templateCode: string) {
    return this.client.delete<DecorationMarketTemplate>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/templates/${encodeURIComponent(templateCode)}`
    )
  }

  restoreAdminDecorationTemplate(tenantId: number, moduleCode: string, templateCode: string) {
    return this.client.post<DecorationMarketTemplate>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/templates/${encodeURIComponent(templateCode)}/restore`
    )
  }

  deleteAdminDecorationTemplatePermanently(tenantId: number, moduleCode: string, templateCode: string) {
    return this.client.delete<void>(
      `/api/admin/decorations/${tenantId}/${encodeURIComponent(moduleCode)}/templates/${encodeURIComponent(templateCode)}/permanent`
    )
  }

  saveAdminDecorationBusinessTemplateVersion(
    tenantId: number,
    moduleCode: string,
    pageCode: string,
    body: SaveDecorationTemplateVersionRequest
  ) {
    return this.client.post<DecorationTemplateVersion>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}/business-template-versions`,
      body
    )
  }

  getAdminDecorationTemplateBinding(tenantId: number, moduleCode: string, pageCode: string) {
    return this.client.get<DecorationPageTemplateBinding | null>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}/template-binding`
    )
  }

  getAdminDecorationBusinessTemplateVersions(tenantId: number, moduleCode: string, templateCode: string) {
    return this.client.get<DecorationTemplateVersion[]>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/business-templates/${encodeURIComponent(templateCode)}/versions`
    )
  }

  updateAdminDecorationBusinessTemplateVersionStatus(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    templateVersion: string,
    body: UpdateDecorationTemplateVersionStatusRequest
  ) {
    return this.client.put<DecorationTemplateVersion>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/business-templates/${encodeURIComponent(templateCode)}/versions/${encodeURIComponent(templateVersion)}/status`,
      body
    )
  }

  installAdminDecorationBusinessTemplateVersion(
    tenantId: number,
    moduleCode: string,
    templateCode: string,
    templateVersion: string,
    body: InstallDecorationTemplateVersionRequest
  ) {
    return this.client.post<DecorationPage>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/business-templates/${encodeURIComponent(templateCode)}/versions/${encodeURIComponent(templateVersion)}/install`,
      body
    )
  }

  previewAdminDecorationTemplateUpgrade(
    tenantId: number,
    moduleCode: string,
    pageCode: string,
    body: PreviewDecorationTemplateUpgradeRequest
  ) {
    return this.client.post<DecorationTemplateUpgradePreview>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}/template-upgrade/preview`,
      body
    )
  }

  applyAdminDecorationTemplateUpgrade(
    tenantId: number,
    moduleCode: string,
    pageCode: string,
    body: ApplyDecorationTemplateUpgradeRequest
  ) {
    return this.client.post<DecorationPage>(
      `/api/admin/decorations/${encodeURIComponent(String(tenantId))}/${encodeURIComponent(moduleCode)}/${encodeURIComponent(pageCode)}/template-upgrade/apply`,
      body
    )
  }
}
